import React, { useState, useEffect } from 'react';
import axios from './lib/adminApi';
import './UserActivity.css';
import config from './config';
import { formatMoney } from './lib/currency';

interface User {
  id: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber: string;
  username?: string;
  venmoUsername?: string;
  profilePictureUrl?: string;
}

interface Bill {
  id: string;
  title: string;
  total: number;
  status: string;
}

interface ActivityEvent {
  id: string;
  type: string;
  timestamp: string;
  userId: string;
  billId?: string;
  metadata: {
    participantName?: string;
    amount?: number;
    billTitle?: string;
    platform?: string;
    imageCount?: number;
    itemCount?: number;
    total?: number;
    businessName?: string;
    durationMs?: number;
    [key: string]: any;
  };
}

interface UserActivityProps {
  userId: string;
}

const UserActivity: React.FC<UserActivityProps> = ({ userId }) => {
  const [user, setUser] = useState<User | null>(null);
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [userRes, activityRes, billsRes] = await Promise.all([
        axios.get(`${config.apiBaseUrl}/admin-dashboard/users`),
        axios.get(`${config.apiBaseUrl}/admin-dashboard/users/${userId}/activity?limit=500`),
        axios.get(`${config.apiBaseUrl}/admin-dashboard/bills`)
      ]);

      if (userRes.data.success) {
        const foundUser = userRes.data.users.find((u: User) => u.id === userId);
        setUser(foundUser || null);
      }

      if (activityRes.data.success) {
        setEvents(activityRes.data.events || []);
      }

      if (billsRes.data.success) {
        setBills(billsRes.data.bills || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getBillInfo = (billId: string | undefined): { title: string; total: number } | null => {
    if (!billId) return null;
    const bill = bills.find(b => b.id === billId);
    return bill ? { title: bill.title, total: bill.total } : null;
  };

  const formatEventType = (type: string): { label: string; emoji: string; color: string; description: string } => {
    const eventMap: { [key: string]: { label: string; emoji: string; color: string; description: string } } = {
      // User actions
      'gallery_opened': { label: 'Opened Gallery', emoji: '📷', color: '#6c757d', description: 'User opened photo gallery' },
      'photo_selected': { label: 'Selected Photo', emoji: '🖼️', color: '#6c757d', description: 'User selected a photo' },
      'camera_opened': { label: 'Opened Camera', emoji: '📸', color: '#6c757d', description: 'User opened camera' },
      'photo_captured': { label: 'Captured Photo', emoji: '📸', color: '#6c757d', description: 'User took a photo' },

      // Upload flow
      'receipt_upload_started': { label: 'Upload Started', emoji: '⬆️', color: '#17a2b8', description: 'Started uploading receipt' },
      'receipt_upload_completed': { label: 'Upload Completed', emoji: '✅', color: '#28a745', description: 'Receipt uploaded successfully' },
      'receipt_upload_failed': { label: 'Upload Failed', emoji: '❌', color: '#dc3545', description: 'Receipt upload failed' },

      // OCR flow
      'receipt_ocr_started': { label: 'OCR Started', emoji: '🔍', color: '#17a2b8', description: 'Started scanning receipt' },
      'receipt_ocr_completed': { label: 'OCR Completed', emoji: '✅', color: '#28a745', description: 'Receipt scanned successfully' },
      'receipt_ocr_failed': { label: 'OCR Failed', emoji: '❌', color: '#dc3545', description: 'Receipt scanning failed' },

      // Bill lifecycle
      'bill_draft_created': { label: 'Draft Created', emoji: '📝', color: '#6f42c1', description: 'Bill draft created' },
      'bill_created': { label: 'Bill Created', emoji: '📋', color: '#007bff', description: 'Bill created with items' },
      'bill_finalized': { label: 'Bill Finalized', emoji: '✨', color: '#6f42c1', description: 'Bill ready for payment' },
      'bill_updated': { label: 'Bill Updated', emoji: '📝', color: '#6c757d', description: 'Bill was updated' },
      'bill_settled': { label: 'Bill Settled', emoji: '🎉', color: '#28a745', description: 'All payments complete' },
      'bill_deleted': { label: 'Bill Deleted', emoji: '🗑️', color: '#dc3545', description: 'Bill was deleted' },

      // Payment events
      'payment_self_marked': { label: 'Marked as Paid', emoji: '💸', color: '#ffc107', description: 'Participant marked themselves paid' },
      'payment_confirmed': { label: 'Payment Confirmed', emoji: '✅', color: '#28a745', description: 'Creator confirmed payment' },
      'payment_requested': { label: 'Payment Requested', emoji: '📤', color: '#17a2b8', description: 'Requested payment via Venmo' },
      'payment_reverted': { label: 'Payment Reverted', emoji: '↩️', color: '#dc3545', description: 'Payment status reset' },

      // Participant events
      'participant_added': { label: 'Participant Added', emoji: '👤', color: '#17a2b8', description: 'Added participant to bill' },
      'participant_removed': { label: 'Participant Removed', emoji: '👤', color: '#6c757d', description: 'Removed participant from bill' },

      // Sharing events
      'bill_shared': { label: 'Bill Shared', emoji: '🔗', color: '#007bff', description: 'Share link created' },
      'bill_public_view': { label: 'Public View', emoji: '👁️', color: '#6c757d', description: 'Someone viewed bill link' },
    };
    return eventMap[type] || { label: type.replace(/_/g, ' '), emoji: '📋', color: '#6c757d', description: type };
  };

  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getUserName = (): string => {
    if (!user) return 'Unknown User';
    return user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username || 'Unknown';
  };

  // Group events by date
  const eventsByDate: { [date: string]: ActivityEvent[] } = {};
  events.forEach(event => {
    const date = new Date(event.timestamp).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    if (!eventsByDate[date]) {
      eventsByDate[date] = [];
    }
    eventsByDate[date].push(event);
  });

  if (loading) {
    return (
      <div className="user-activity-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading activity...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="user-activity-page">
      <div className="activity-page-header">
        <a href="/admin/dash" className="back-link">← Back to Dashboard</a>
        <div className="user-info-header">
          {user?.profilePictureUrl ? (
            <img src={user.profilePictureUrl} alt={getUserName()} className="user-avatar" />
          ) : (
            <div className="user-avatar-placeholder">
              {user?.firstName?.charAt(0) || user?.name?.charAt(0) || '?'}
            </div>
          )}
          <div className="user-info-text">
            <h1>{getUserName()}'s Activity</h1>
            <p className="user-meta">
              @{user?.username || user?.venmoUsername || 'N/A'} • {events.length} events in last 90 days
            </p>
          </div>
        </div>
      </div>

      <div className="activity-page-content">
        {events.length === 0 ? (
          <div className="empty-state">
            <p>No activity recorded for this user yet.</p>
            <p className="empty-note">Events are only recorded going forward from when tracking was enabled.</p>
          </div>
        ) : (
          Object.entries(eventsByDate).map(([date, dateEvents]) => (
            <div key={date} className="date-group">
              <div className="date-header">{date}</div>
              <div className="events-list">
                {dateEvents.map((event) => {
                  const { label, emoji, color, description } = formatEventType(event.type);
                  const billInfo = getBillInfo(event.billId);
                  const time = new Date(event.timestamp).toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    second: '2-digit',
                    hour12: true
                  });

                  return (
                    <div key={event.id} className="event-row">
                      <div className="event-time">{time}</div>
                      <div className="event-indicator">
                        <div className="event-dot" style={{ backgroundColor: color }}></div>
                        <div className="event-line"></div>
                      </div>
                      <div className="event-card">
                        <div className="event-header">
                          <span className="event-emoji">{emoji}</span>
                          <span className="event-label" style={{ color }}>{label}</span>
                        </div>
                        <div className="event-description">{description}</div>

                        {billInfo && (
                          <div className="event-bill-info">
                            <span className="bill-icon">📋</span>
                            <span className="bill-title">{billInfo.title}</span>
                            <span className="bill-total">{formatMoney(billInfo.total)}</span>
                          </div>
                        )}

                        {event.billId && (
                          <div className="event-bill-id">
                            Bill ID: <code>{event.billId}</code>
                          </div>
                        )}

                        <div className="event-metadata">
                          {event.metadata.platform && (
                            <span className="meta-tag">📱 {event.metadata.platform}</span>
                          )}
                          {event.metadata.imageCount && (
                            <span className="meta-tag">🖼️ {event.metadata.imageCount} image(s)</span>
                          )}
                          {event.metadata.itemCount && (
                            <span className="meta-tag">📝 {event.metadata.itemCount} items</span>
                          )}
                          {event.metadata.total && (
                            <span className="meta-tag">💰 {formatMoney(event.metadata.total)}</span>
                          )}
                          {event.metadata.durationMs && (
                            <span className="meta-tag">⏱️ {formatDuration(event.metadata.durationMs)}</span>
                          )}
                          {event.metadata.businessName && (
                            <span className="meta-tag">🏪 {event.metadata.businessName}</span>
                          )}
                          {event.metadata.participantName && (
                            <span className="meta-tag">👤 {event.metadata.participantName}</span>
                          )}
                          {event.metadata.amount && (
                            <span className="meta-tag">💵 {formatMoney(event.metadata.amount)}</span>
                          )}
                        </div>

                        <div className="event-id">
                          Event ID: <code>{event.id}</code>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default UserActivity;
